import React from 'react';
import {
    ArrowForwardOutlined,
    Badge,
    Box,
    Button,
    FeatureCard,
    Group,
    Image,
    spacing,
    Text,
    useBreakpoint,
    radius,
    useHover,
} from '@landr/maestro';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { CONFIG } from 'utils/config';
import { PromotionalCardType, Scalars } from 'types/generated/graphql';
import { analytics } from 'utils';
import {
    CtaGoal,
    CtaType,
    InteractionSource,
} from 'utils/analytics/AnalyticsV2Service.generated';
import { useMilestonesController } from 'controllers/business/Milestones/Milestones.controller';
import { useToLocalizedPath } from 'hooks/useToLocalizedPath/useToLocalizedPath';

const StyledFeatureCard = styled(FeatureCard)`
    overflow: hidden;
    border-radius: ${radius('base')};
    min-height: 190px;
    &:hover {
        cursor: pointer;
    }
`;

export const StyledImage = styled(Image)`
    position: absolute;
    width: 150%;
    right: -${spacing('sm')};
    top: -${spacing('sm')};
`;

export const StyledImageText = styled(Text)`
    width: 50%;
    ${({ theme }) => theme.breakpoint('sm')`
      width: 100%;
      margin-top: 100px;
    `}
`;

export interface PromotionalCardProps {
    title: string;
    description: string;
    ctaText: string;
    ctaUrl: string;
    backgroundColor: string;
    imageUrl: string;
    imageHoverUrl?: string;
    type: PromotionalCardType;
    id: Scalars['ID'];
}

const _PromotionalCard = ({
    isTablet,
    isMobile,
    backgroundColor,
    ctaText,
    imageUrl,
    description,
    title,
    onClick,
}: {
    isTablet: boolean;
    isMobile: boolean;
    backgroundColor: string;
    ctaText: string;
    imageUrl: string;
    description: string;
    title: string;
    onClick: () => void;
}) => {
    return (
        <StyledFeatureCard
            onClick={onClick}
            maxHeight="300px"
            minHeight={isTablet ? '350px' : '300px'}
            badges={[
                <Box key={1} p={isMobile ? 0 : 'lg'}>
                    <Badge content={title} kind="subtle" size="md" />
                </Box>,
            ]}
            backgroundColor={backgroundColor ?? undefined}
            actions={[
                <Box key={1} p={isMobile ? 0 : 'lg'}>
                    <Group
                        direction="horizontal"
                        gutter="xs"
                        alignItems="center"
                    >
                        <Button
                            iconRight={ArrowForwardOutlined}
                            kind="link"
                            variant="secondary"
                        >
                            {ctaText}
                        </Button>
                    </Group>
                </Box>,
            ]}
            media={<StyledImage src={imageUrl} loading="lazy" />}
        >
            <StyledImageText pl={isMobile ? 0 : 'lg'}>
                <Text size="xl">{description}</Text>
            </StyledImageText>
        </StyledFeatureCard>
    );
};

export const PromotionalCard: React.FC<PromotionalCardProps> = ({
    title,
    description,
    ctaText,
    ctaUrl,
    backgroundColor,
    imageUrl,
    imageHoverUrl,
    type,
    id,
}) => {
    const isTablet = useBreakpoint('lg');
    const isMobile = useBreakpoint('md');
    const { perform } = useMilestonesController();

    const toLocalizedPath = useToLocalizedPath();

    const ctaNewUrl = new URL(ctaUrl, CONFIG.VITE_SAMPLES_WEB_URL);
    const isSamplesUrl = ctaNewUrl.origin === CONFIG.VITE_SAMPLES_WEB_URL;

    const to = isSamplesUrl
        ? toLocalizedPath(ctaNewUrl.pathname)
        : ctaNewUrl.href;

    const [ref, isHovered] = useHover(false);

    const showHover = imageHoverUrl && isHovered;
    const backgroundImage = showHover ? imageHoverUrl : imageUrl;

    const handleClick = () => {
        analytics.trackCTAClicked({
            ctaGoal: CtaGoal.ViewPromoCard,
            ctaText,
            ctaType: CtaType.Button,
            interactionSource: InteractionSource.CardAction,
            ctaDestination: to,
        });

        if (type === PromotionalCardType.None) {
            perform(id, true);
        }
    };

    return (
        <Box ref={ref}>
            {isSamplesUrl ? (
                <Link to={to}>
                    <_PromotionalCard
                        isTablet={isTablet}
                        isMobile={isMobile}
                        title={title}
                        description={description}
                        ctaText={ctaText}
                        backgroundColor={backgroundColor}
                        imageUrl={backgroundImage}
                        onClick={handleClick}
                    />
                </Link>
            ) : (
                <a href={to} target="_blank">
                    <_PromotionalCard
                        isTablet={isTablet}
                        isMobile={isMobile}
                        title={title}
                        description={description}
                        ctaText={ctaText}
                        backgroundColor={backgroundColor}
                        imageUrl={backgroundImage}
                        onClick={handleClick}
                    />
                </a>
            )}
        </Box>
    );
};

export default PromotionalCard;
